/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { z } from "zod";

import { apiSlice } from "api/api.slice";
import type { IdArg, SimilarDimensionArg, TopicMetadataResult } from "api/api.types";
import { QUERY_LIMIT } from "api/constants";
import { liveBriefingSampleResponseSchema } from "api/schemas/briefingsApiSchema";
import { topicMetadataResultSchema, topicSchema } from "api/schemas/topicsApiSchema";
import { toSearchString } from "api/utils";
import type { LiveBriefingSampleResponse } from "domains/briefings/types";
import type { TopicVisibilityPatch } from "domains/topicshare/topicshare.types";
import type { DestinationTypes } from "domains/types";
import {
  type CustomMapOwner,
  followTopic,
  followTopicFailure,
  followTopicSuccess,
  unfollowTopic,
  unfollowTopicFailure,
  unfollowTopicSuccess,
} from "entities/topics/actions";

type TopicCuratedArg = {
  search: { canEdit?: boolean; limit: number; offset: number; owner?: CustomMapOwner; type?: DestinationTypes };
};

type PatchTopicVisibilityArg = { body: TopicVisibilityPatch; topic_id: string };

type DeleteTopicAccessArg = { topic_id: string };

type DeleteTopicArg = { topic_id: string };

export const topicsApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCustomMaps: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      providesTags: ["CustomMaps"],
      query: ({ pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/topic/curated",
          params: {
            limit: QUERY_LIMIT,
            offset,
            types: "Custom Maps",
            can_edit: true,
            owner: true,
          },
        };
      },
      responseSchema: z.array(topicSchema),
    }),
    getTopic: build.query({
      query: ({ id }: IdArg) => `/topic/${id}`,
      responseSchema: topicSchema,
    }),
    getTopicCurated: build.query({
      query: ({ search }: TopicCuratedArg) => `/topic/curated${toSearchString(search)}`,
      responseSchema: z.array(topicSchema),
    }),
    getTopicMetadata: build.query<TopicMetadataResult, void>({
      query: () => "/topic/metadata",
      responseSchema: topicMetadataResultSchema,
    }),
    getSimilarTopicsByDimension: build.query({
      query: ({ body, search }: SimilarDimensionArg) => ({
        body,
        method: "POST",
        url: `/topic/similar/dimension${toSearchString(search)}`,
      }),
      responseSchema: z.array(topicSchema),
    }),
    patchTopicVisibility: build.mutation<unknown, PatchTopicVisibilityArg>({
      query: ({ body, topic_id }) => ({ body, method: "PATCH", url: `/topic/${topic_id}/visibility` }),
      responseSchema: z.unknown(),
    }),
    deleteTopicAccess: build.query<unknown, DeleteTopicAccessArg>({
      query: ({ topic_id }) => ({ method: "DELETE", url: `/topic/${topic_id}/access` }),
      responseSchema: z.unknown(),
    }),
    deleteTopic: build.mutation<unknown, DeleteTopicArg>({
      query: ({ topic_id }) => ({ method: "DELETE", url: `/topic/${topic_id}` }),
      onQueryStarted: async ({ topic_id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const index = page.findIndex((topic) => topic.id === topic_id);
              if (index !== -1) page.splice(index, 1);
            });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
      responseSchema: z.unknown(),
    }),
    followTopic: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ method: "PUT", url: `/topic/${id}/follow` }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        dispatch(followTopic({ id }));
        const customMapsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = true;
            });
          }),
        );
        try {
          await queryFulfilled;
          dispatch(followTopicSuccess({ id, isFollowing: true }));
        } catch {
          customMapsOptimisticUpdate.undo();
          dispatch(followTopicFailure({ id }, { logoutOnAuthError: true }));
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
      responseSchema: z.unknown(),
    }),
    unfollowTopic: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ method: "DELETE", url: `/topic/${id}/follow` }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        dispatch(unfollowTopic({ id }));
        const customMapsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = false;
            });
          }),
        );
        try {
          await queryFulfilled;
          dispatch(unfollowTopicSuccess({ id, isFollowing: false }));
        } catch {
          customMapsOptimisticUpdate.undo();
          dispatch(unfollowTopicFailure({ id }, { logoutOnAuthError: true }));
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
      responseSchema: z.unknown(),
    }),
    liveBriefingSample: build.query<LiveBriefingSampleResponse, void>({
      query: () => "/topic/live_briefing_sample",
      responseSchema: liveBriefingSampleResponseSchema,
    }),
  }),
});

export const {
  useGetCustomMapsInfiniteQuery,
  useGetTopicQuery,
  useGetTopicCuratedQuery,
  useGetTopicMetadataQuery,
  useGetSimilarTopicsByDimensionQuery,
  useLiveBriefingSampleQuery,
  usePatchTopicVisibilityMutation,
  useDeleteTopicAccessQuery,
  useDeleteTopicMutation,
  useFollowTopicMutation,
  useUnfollowTopicMutation,
  useLazyDeleteTopicAccessQuery,
  useLazyGetSimilarTopicsByDimensionQuery,
  useLazyGetTopicMetadataQuery,
} = topicsApiSlice;
